// @ts-nocheck
import { useState } from 'react';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import {
  Stack,
  Group,
  Text,
  Button,
  Modal,
  TextInput,
  Textarea,
  NumberInput,
  Select,
  ActionIcon,
  Tabs,
  Loader,
  Center,
  Badge,
} from '@mantine/core';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import { DateInput } from '@mantine/dates';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import { notifications } from '@mantine/notifications';
import {
  IconPlus,
  IconEdit,
  IconReceipt,
  IconCheck,
  IconX,
  IconSend,
  IconReportMoney,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '../../components/PageHeader';
import { EmptyState } from '../../components/EmptyState';
import { StatusBadge } from '../../components/StatusBadge';
import { CurrencyDisplay } from '../../components/CurrencyDisplay';
import { DataTable, type Column } from '../../components/DataTable';
import {
  useExpenses,
  useCreateExpense,
  useUpdateExpense,
  useSubmitExpense,
  useApproveExpense,
  useRejectExpense,
} from '../../hooks/useExpenses';
import { formatCurrency, formatDate } from '../../utils/format';
import type {
  Expense,
  ExpenseCategory,
  CreateExpenseInput,
} from '../../types/expense';
import {
  EXPENSE_CATEGORY_LABELS,
  EXPENSE_CATEGORY_COLORS,
} from '../../types/expense';

const EXPENSE_STATUS_COLORS: Record<string, string> = {
  draft: 'gray',
  submitted: 'blue',
  approved: 'green',
  rejected: 'red',
  reimbursed: 'teal',
};

const CATEGORY_OPTIONS = Object.entries(EXPENSE_CATEGORY_LABELS).map(([value, label]) => ({
  value,
  label,
}));

export default function ExpenseListPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [activeTab, setActiveTab] = useState<string | null>('my');
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState<Expense | null>(null);

  // Create/Edit form state
  const [category, setCategory] = useState<string>('travel');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState<number>(0);
  const [vendor, setVendor] = useState('');
  const [expenseDate, setExpenseDate] = useState<string | null>(new Date().toISOString().split('T')[0] ?? null);
  const [receiptUrl, setReceiptUrl] = useState('');

  // Reject form
  const [rejectReason, setRejectReason] = useState('');

  const queryParams = {
    page,
    perPage: 25,
    status: activeTab === 'pending_approval' ? 'submitted' : statusFilter,
    employeeId: undefined as string | undefined,
  };

  const { data: rawExpensesData, isLoading } = useExpenses(queryParams);
  const expensesData = rawExpensesData as any;

  const createExpense = useCreateExpense();
  const updateExpense = useUpdateExpense();
  const submitExpense = useSubmitExpense();
  const approveExpense = useApproveExpense();
  const rejectExpense = useRejectExpense();

  const resetForm = () => {
    setCategory('travel');
    setDescription('');
    setAmount(0);
    setVendor('');
    setExpenseDate(new Date().toISOString().split('T')[0] ?? null);
    setReceiptUrl('');
    setSelectedExpense(null);
  };

  const openCreateModal = () => {
    resetForm();
    setCreateModalOpen(true);
  };

  const openEditModal = (expense: Expense) => {
    setSelectedExpense(expense);
    setCategory(expense.category);
    setDescription(expense.description);
    setAmount(expense.amount);
    setVendor(expense.vendor);
    setExpenseDate(expense.date);
    setReceiptUrl(expense.receiptUrl ?? '');
    setCreateModalOpen(true);
  };

  const openRejectModal = (expense: Expense) => {
    setSelectedExpense(expense);
    setRejectReason('');
    setRejectModalOpen(true);
  };

  const handleCreateOrUpdate = async () => {
    if (!description.trim() || amount <= 0 || !vendor.trim() || !expenseDate) {
      notifications.show({
        title: 'Validation Error',
        message: 'Description, amount, vendor, and date are required.',
        color: 'red',
      });
      return;
    }

    const input: CreateExpenseInput = {
      employeeId: '', // Server assigns based on auth context
      category: category as ExpenseCategory,
      description,
      amount,
      vendor,
      date: expenseDate ?? '',
      receiptUrl: receiptUrl || undefined,
    };

    try {
      if (selectedExpense) {
        await updateExpense.mutateAsync({
          id: selectedExpense.id,
          category: category as ExpenseCategory,
          description,
          amount,
          vendor,
          date: expenseDate ?? '',
          receiptUrl: receiptUrl || undefined,
        });
        notifications.show({ title: 'Expense Updated', message: 'Expense has been updated.', color: 'green' });
      } else {
        await createExpense.mutateAsync(input);
        notifications.show({ title: 'Expense Created', message: 'New expense has been created.', color: 'green' });
      }
      setCreateModalOpen(false);
      resetForm();
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: err instanceof Error ? err.message : 'Failed to save expense.',
        color: 'red',
      });
    }
  };

  const handleSubmit = async (expense: Expense) => {
    try {
      await submitExpense.mutateAsync({ id: expense.id });
      notifications.show({ title: 'Expense Submitted', message: 'Expense submitted for approval.', color: 'blue' });
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: err instanceof Error ? err.message : 'Failed to submit expense.',
        color: 'red',
      });
    }
  };

  const handleApprove = async (expense: Expense) => {
    try {
      await approveExpense.mutateAsync({ id: expense.id });
      notifications.show({ title: 'Expense Approved', message: `Expense of ${formatCurrency(expense.amount)} approved.`, color: 'green' });
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: err instanceof Error ? err.message : 'Failed to approve expense.',
        color: 'red',
      });
    }
  };

  const handleReject = async () => {
    if (!selectedExpense || !rejectReason.trim()) return;
    try {
      await rejectExpense.mutateAsync({ id: selectedExpense.id, reason: rejectReason });
      notifications.show({ title: 'Expense Rejected', message: 'Expense has been rejected.', color: 'orange' });
      setRejectModalOpen(false);
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: err instanceof Error ? err.message : 'Failed to reject expense.',
        color: 'red',
      });
    }
  };

  const columns: Column<Expense>[] = [
    {
      key: 'date',
      label: 'Date',
      sortable: true,
      width: 110,
      render: (exp) => formatDate(exp.date),
    },
    {
      key: 'employeeName',
      label: 'Employee',
      sortable: true,
      render: (exp) => exp.employeeName,
    },
    {
      key: 'category',
      label: 'Category',
      width: 160,
      render: (exp) => (
        <Badge
          color={EXPENSE_CATEGORY_COLORS[exp.category] ?? 'gray'}
          variant="light"
          size="sm"
        >
          {EXPENSE_CATEGORY_LABELS[exp.category] ?? exp.category}
        </Badge>
      ),
    },
    {
      key: 'vendor',
      label: 'Vendor',
      render: (exp) => exp.vendor,
    },
    {
      key: 'description',
      label: 'Description',
      render: (exp) => (
        <Text size="sm" lineClamp={1}>{exp.description}</Text>
      ),
    },
    {
      key: 'amount',
      label: 'Amount',
      width: 110,
      render: (exp) => <CurrencyDisplay amount={exp.amount} size="sm" />,
    },
    {
      key: 'status',
      label: 'Status',
      width: 120,
      render: (exp) => (
        <Group gap={4}>
          <StatusBadge status={exp.status} colorMap={EXPENSE_STATUS_COLORS} />
          {exp.policyViolations.length > 0 && (
            <Badge color="orange" variant="dot" size="xs" title={exp.policyViolations.join(', ')}>
              {exp.policyViolations.length}
            </Badge>
          )}
        </Group>
      ),
    },
  ];

  return (
    <Stack gap="md">
      <PageHeader
        title="Expense Management"
        subtitle={expensesData?.expenses ? `${expensesData.expenses.total} expenses` : undefined}
        actions={
          <Group gap="sm">
            <Button
              variant="outline"
              icon={<IconReportMoney size={16} />}
              onClick={() => navigate('/admin/workforce/expenses/reports')}
            >
              Expense Reports
            </Button>
            <Button
              icon={<IconPlus size={16} />}
              onClick={openCreateModal}
            >
              New Expense
            </Button>
          </Group>
        }
      />

      <Tabs value={activeTab} onChange={(v) => { setActiveTab(v); setPage(1); setStatusFilter(undefined); }}>
        <Tabs.List>
          <Tabs.Tab value="my">My Expenses</Tabs.Tab>
          <Tabs.Tab value="pending_approval">Pending Approval</Tabs.Tab>
          <Tabs.Tab value="all">All Expenses</Tabs.Tab>
        </Tabs.List>
      </Tabs>

      {activeTab !== 'pending_approval' && (
        <Group gap="sm">
          <Select
            placeholder="Filter by status"
            clearable
            value={statusFilter ?? null}
            onChange={(v) => { setStatusFilter(v ?? undefined); setPage(1); }}
            data={[
              { value: 'draft', label: 'Draft' },
              { value: 'submitted', label: 'Submitted' },
              { value: 'approved', label: 'Approved' },
              { value: 'rejected', label: 'Rejected' },
              { value: 'reimbursed', label: 'Reimbursed' },
            ]}
            w={180}
          />
          <Select
            placeholder="Filter by category"
            clearable
            data={CATEGORY_OPTIONS}
            w={200}
          />
        </Group>
      )}

      {isLoading ? (
        <Center py="xl"><Loader /></Center>
      ) : !expensesData?.expenses?.data.length ? (
        <EmptyState
          icon={<IconReceipt size={48} stroke={1.5} color="var(--mantine-color-dimmed)" />}
          title="No expenses found"
          message={
            activeTab === 'pending_approval'
              ? 'No expenses are pending your approval.'
              : 'Track your business expenses by creating your first entry.'
          }
          action={activeTab !== 'pending_approval' ? { label: 'Create Expense', onClick: openCreateModal } : undefined}
        />
      ) : (
        <DataTable<Expense>
          columns={columns}
          data={expensesData.expenses.data}
          loading={isLoading}
          pagination={{
            page,
            pageSize: 25,
            total: expensesData.expenses.total,
            onPageChange: setPage,
          }}
          getRowKey={(exp) => exp.id}
          emptyMessage="No expenses found"
          actions={(exp) => (
            <Group gap={4} wrap="nowrap">
              {exp.status === 'draft' && (
                <>
                  <ActionIcon
                    variant="subtle"
                    size="sm"
                    color="blue"
                    onClick={() => openEditModal(exp)}
                    title="Edit"
                  >
                    <IconEdit size={16} />
                  </ActionIcon>
                  <ActionIcon
                    variant="subtle"
                    size="sm"
                    color="teal"
                    onClick={() => handleSubmit(exp)}
                    title="Submit for Approval"
                  >
                    <IconSend size={16} />
                  </ActionIcon>
                </>
              )}
              {exp.status === 'submitted' && activeTab === 'pending_approval' && (
                <>
                  <ActionIcon
                    variant="subtle"
                    size="sm"
                    color="green"
                    onClick={() => handleApprove(exp)}
                    title="Approve"
                  >
                    <IconCheck size={16} />
                  </ActionIcon>
                  <ActionIcon
                    variant="subtle"
                    size="sm"
                    color="red"
                    onClick={() => openRejectModal(exp)}
                    title="Reject"
                  >
                    <IconX size={16} />
                  </ActionIcon>
                </>
              )}
            </Group>
          )}
        />
      )}

      {/* Create/Edit Expense Modal */}
      <Modal
        opened={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        title={selectedExpense ? 'Edit Expense' : 'New Expense'}
        size="md"
      >
        <Stack gap="md">
          <Select
            label="Category"
            value={category}
            onChange={(v) => setCategory(v ?? 'travel')}
            data={CATEGORY_OPTIONS}
            required
          />

          <TextInput
            label="Description"
            placeholder="Brief description of the expense"
            value={description}
            onChange={(e) => setDescription(e.currentTarget.value)}
            required
          />

          <NumberInput
            label="Amount"
            value={amount}
            onChange={(val) => setAmount(Number(val) || 0)}
            min={0.01}
            decimalScale={2}
            prefix="$"
            required
          />

          <TextInput
            label="Vendor"
            placeholder="Business or merchant name"
            value={vendor}
            onChange={(e) => setVendor(e.currentTarget.value)}
            required
          />

          <DateInput
            label="Date"
            value={expenseDate}
            onChange={setExpenseDate}
            required
            maxDate={new Date()}
          />

          <TextInput
            label="Receipt URL"
            placeholder="Link to uploaded receipt (optional)"
            value={receiptUrl}
            onChange={(e) => setReceiptUrl(e.currentTarget.value)}
          />

          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={() => setCreateModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateOrUpdate}
              loading={createExpense.isPending || updateExpense.isPending}
            >
              {selectedExpense ? 'Update Expense' : 'Create Expense'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Reject Expense Modal */}
      <Modal
        opened={rejectModalOpen}
        onClose={() => setRejectModalOpen(false)}
        title="Reject Expense"
        size="sm"
      >
        <Stack gap="md">
          {selectedExpense && (
            <Group justify="space-between">
              <Text size="sm" c="dimmed">{selectedExpense.description}</Text>
              <CurrencyDisplay amount={selectedExpense.amount} size="sm" />
            </Group>
          )}

          <Textarea
            label="Reason for rejection"
            placeholder="Provide a reason for rejecting this expense"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.currentTarget.value)}
            rows={3}
            required
          />

          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={() => setRejectModalOpen(false)}>
              Cancel
            </Button>
            <Button
              color="red"
              onClick={handleReject}
              loading={rejectExpense.isPending}
              disabled={!rejectReason.trim()}
            >
              Reject Expense
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
